import { PLACEHOLDER_VALUE } from '@/constants';
import { useDateFormatter } from '@/hooks';
import { Study } from '@/types';
import { Avatar, Grid, GridItem, Tooltip } from '@nautilusbio/nb-design-system';
import { Text } from '@nautilusbio/nb-design-system/Text';
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from '../styles.module.css';

interface StudyMetadataTableProps {
  study: Study;
  emptyText?: string;
}

interface MetadataRow {
  label: string;
  date: string;
  byName: string | null | undefined;
  show?: boolean;
}

/**
 * Study metadata table component that displays created/updated information.
 * Each row shows: Label | Date | Avatar (tooltip = full name)
 * Uses the user's date format preference from the app store via useDateFormatter hook.
 */
export function StudyMetadataTable({ study, emptyText = PLACEHOLDER_VALUE }: StudyMetadataTableProps) {
  const { t } = useTranslation(['studies', 'general']);
  const formatDate = useDateFormatter();

  const metadataRows: MetadataRow[] = useMemo(
    () => [
      {
        label: t('general:created'),
        date: formatDate(study?.created_at) || emptyText,
        byName: study?.created_by_full_name,
        show: true,
      },
      {
        label: t('general:updated'),
        date: formatDate(study?.updated_at) || emptyText,
        byName: study?.updated_by_full_name,
        show: !!study?.updated_at,
      },
    ],
    [emptyText, formatDate, study, t],
  );

  // Determine if any visible row has byName data to adjust grid columns
  const visibleRows = metadataRows.filter(row => row.show);
  const hasAnyByName = visibleRows.some(row => row.byName);
  const gridTemplate = hasAnyByName ? 'auto 1fr auto' : 'auto 1fr';

  return (
    <Grid
      alignItems="center"
      className={styles.metadataGrid}
      gap={2}
      gridTemplateColumns={gridTemplate}
      justifyItems="start"
    >
      {metadataRows
        .filter(row => row.show)
        .map((row, index) => (
          <Fragment key={`${row.label}-${index}`}>
            <GridItem className={styles.metadataLabel}>
              <Text as="strong" noWrap>
                {row.label}:
              </Text>
            </GridItem>

            <GridItem className={styles.metadataValue}>
              <Text as="span" noWrap>
                {row.date}
              </Text>
            </GridItem>

            {row?.byName ? (
              <GridItem className={styles.metadataAvatar}>
                <Tooltip
                  content={row.byName}
                  label={row.byName}
                  placement="right-start"
                  trigger={
                    <Avatar hasTooltip size="xs3">
                      {row.byName}
                    </Avatar>
                  }
                />
              </GridItem>
            ) : hasAnyByName ? (
              <GridItem className={styles.metadataAvatar} />
            ) : null}
          </Fragment>
        ))}
    </Grid>
  );
}
